'use client';

import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

type SearchCommandContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  restoreFocus: () => void;
};

const SearchCommandContext = createContext<SearchCommandContextValue | null>(
  null
);

/**
 * Shared open/closed state for the global recipe/ingredient search palette,
 * so the header trigger (desktop) and NavigationBar trigger (mobile) open the
 * same dialog instance instead of each mounting their own. Also wires the
 * ⌘K/Ctrl+K shortcut globally.
 */
export const SearchCommandProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const [open, updateOpen] = useState(false);
  const trigger = useRef<HTMLElement | null>(null);
  const setOpen = useCallback((next: boolean) => {
    if (next)
      trigger.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
    updateOpen(next);
  }, []);
  const restoreFocus = useCallback(() => {
    if (trigger.current?.isConnected)
      trigger.current.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, setOpen]);

  const value = useMemo(
    () => ({ open, setOpen, restoreFocus }),
    [open, setOpen, restoreFocus]
  );

  return (
    <SearchCommandContext.Provider value={value}>
      {children}
    </SearchCommandContext.Provider>
  );
};

export function useSearchCommand(): SearchCommandContextValue {
  const ctx = useContext(SearchCommandContext);
  if (!ctx) {
    throw new Error(
      'useSearchCommand must be used within a SearchCommandProvider'
    );
  }
  return ctx;
}
